// utils/api.js
// Hjälpfunktioner för API-anrop med JWT-autentisering

import { jwtDecode } from 'jwt-decode';

/**
 * Kontrollerar om en token är giltig (inte expired)
 * @param {string} token - JWT token
 * @returns {boolean} true om token är giltig, false annars
 */
const isTokenValid = (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Konvertera till sekunder
    
    // Kontrollera om token har exp-claim och om den är expired
    if (decoded.exp && decoded.exp < currentTime) {
      return false;
    }
    return true;
  } catch (error) {
    // Om token inte kan dekodas, är den ogiltig
    return false;
  }
};

/**
 * Hämtar Authorization header med JWT-token från localStorage
 * Kontrollerar också om token är giltig
 * @returns {string} Authorization header string eller null om ingen token finns eller token är ogiltig
 */
export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  if (token) {
    // Kontrollera om token är giltig
    if (!isTokenValid(token)) {
      localStorage.removeItem('token');
      return null;
    }
    return `Bearer ${token}`;
  }
  return null;
};

/**
 * Skapar headers-objekt med Authorization header för API-anrop
 * @param {object} additionalHeaders - Ytterligare headers att inkludera
 * @returns {object} Headers-objekt med Authorization header
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
  const authHeader = getAuthHeader();
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };
  
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  
  return headers;
};

/**
 * Bas-URL för uppladdning och borttagning av bilder.
 * I lokal miljö: sätt REACT_APP_UPLOAD_URL till produktionens API (t.ex. https://seglingssajten.se) i .env.local
 * så att bilder lagras på samma ställe som i produktion.
 */
export const getUploadApiBaseUrl = () =>
  (process.env.REACT_APP_UPLOAD_URL || process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');

/**
 * Wrapper för fetch som automatiskt hanterar 401-fel (ogiltig token)
 * Loggar automatiskt ut användaren om token är ogiltig
 * @param {string} url - URL att hämta från
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Lägg till Authorization header om den inte redan finns
  if (!options.headers) {
    options.headers = {};
  }
  
  const authHeader = getAuthHeader();
  if (authHeader && !options.headers['Authorization']) {
    options.headers['Authorization'] = authHeader;
  }
  
  const response = await fetch(url, options);
  
  // Om vi får 401, token är ogiltig - logga ut användaren
  if (response.status === 401) {
    console.warn('401 Unauthorized - token är ogiltig, loggar ut användaren');
    localStorage.removeItem('token');
    // Trigger en reload så att AuthContext uppdateras
    window.location.href = '/info';
  }
  
  return response;
};

/**
 * Returnerar full URL för en bild (lagrad URL, relativ sökväg eller gammal /images/...).
 * @param {string} stored - Lagrad URL (https://...), relativ sökväg (profiles/...), eller gammal /images/filnamn
 * @returns {string} URL som kan användas i <img src={...} />
 */
export const getImageDisplayUrl = (stored) => {
  if (!stored || typeof stored !== 'string') return '';
  if (stored.startsWith('http://') || stored.startsWith('https://')) return stored;
  if (stored.startsWith('/images/')) {
    const server = process.env.REACT_APP_SERVER_URL || '';
    return server ? `${server}/api${stored}` : stored;
  }
  const base = process.env.REACT_APP_IMAGES_BASE_URL || process.env.REACT_APP_SERVER_URL || '';
  const pathPart = stored.replace(/^\/+/, '');
  return base ? `${base.replace(/\/$/, '')}/${pathPart}` : stored;
};

